"use client"

import { type FormEvent, useLayoutEffect, useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Loader2, UserMinus, X } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { PersonIdentityCell } from "@/components/person-identity-cell"
import { EntityDetailSkeleton } from "@/components/layout/entity-detail-skeleton"
import { InviteByEmailField } from "@/components/employer/invite-by-email-field"
import { SettingsPageShell } from "@/components/settings/settings-page-shell"
import { SettingsPanel } from "@/components/settings/settings-panel"
import { SettingsPanelEmpty } from "@/components/settings/settings-panel-empty"
import { useManagedOrganisationDetail } from "@/hooks/use-organisations"
import {
  useOrganisationTeam,
  type OrganisationInvitation,
  type OrganisationMember,
} from "@/hooks/use-organisation-team"
import { useEmployerOrganisationOptional } from "@/context/employer-organisation-context"
import {
  employerOrganisationHomePath,
  managedEmployerOrgSlugStorageKey,
} from "@/constants"
import {
  EMPLOYER_ORGANISATION_SETTINGS_TEAM_SEGMENT,
  getEmployerOrganisationSettingsSection,
} from "@/constants/employer-organisation-settings-nav"
import { navigateWithThreadPreservation } from "@/utils/thread-navigation"

type EmployerOrganisationTeamPageProps = {
  slug?: string
}

type TeamListMember = {
  kind: "member"
  key: string
  member: OrganisationMember
}

type TeamListPending = {
  kind: "pending"
  key: string
  invitation: OrganisationInvitation
}

type TeamListItem = TeamListMember | TeamListPending

function buildTeamList(
  members: OrganisationMember[],
  invitations: OrganisationInvitation[],
): TeamListItem[] {
  const memberEmails = new Set(members.map((m) => m.email.toLowerCase()))
  const items: TeamListItem[] = members.map((member) => ({
    kind: "member",
    key: `member-${member.id}`,
    member,
  }))

  for (const invitation of invitations) {
    if (memberEmails.has(invitation.email.toLowerCase())) continue
    items.push({
      kind: "pending",
      key: `pending-${invitation.id}`,
      invitation,
    })
  }

  return items
}

export function EmployerOrganisationTeamPage({ slug: slugProp }: EmployerOrganisationTeamPageProps) {
  const { slug: slugParam } = useParams<{ slug: string }>()
  const routeSlug = slugProp?.trim() || slugParam?.trim() || ""
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const ctx = useEmployerOrganisationOptional()
  const homePath = employerOrganisationHomePath()

  const teamSection = getEmployerOrganisationSettingsSection(
    EMPLOYER_ORGANISATION_SETTINGS_TEAM_SEGMENT,
  )

  const { data: organisation, isLoading, error } = useManagedOrganisationDetail(routeSlug || undefined)
  const {
    members,
    invitations,
    isLoadingMembers,
    isLoadingInvitations,
    invitationsError,
    inviteMember,
    isInviting,
    revokeInvitation,
    isRevokingInvitation,
    removeMember,
    isRemovingMember,
  } = useOrganisationTeam(routeSlug || undefined)

  const [inviteEmail, setInviteEmail] = useState("")

  const teamList = useMemo(() => buildTeamList(members, invitations), [members, invitations])
  const isLoadingTeam = isLoadingMembers || isLoadingInvitations
  const isLastMember = members.length <= 1

  useLayoutEffect(() => {
    if (!routeSlug || !ctx?.enabled) return
    try {
      window.localStorage.setItem(managedEmployerOrgSlugStorageKey(), routeSlug)
    } catch {
      /* ignore */
    }
    ctx.setActiveOrganisationSlug(routeSlug)
  }, [routeSlug, ctx])

  const handleInvite = (event: FormEvent) => {
    event.preventDefault()
    const email = inviteEmail.trim().toLowerCase()
    if (!email) {
      toast.error("Enter an email address")
      return
    }

    inviteMember(email, {
      onSuccess: () => {
        setInviteEmail("")
        toast.success("Invitation sent")
      },
      onError: (err) => {
        const message = err instanceof Error ? err.message : "Failed to send invitation"
        toast.error(message)
      },
    })
  }

  const handleRemoveMember = (memberId: string, memberEmail: string, isSelf: boolean) => {
    const label = isSelf ? "yourself" : memberEmail
    if (!window.confirm(`Remove ${label} from this organisation?`)) return

    removeMember(memberId, {
      onSuccess: () => {
        toast.success(isSelf ? "You left the organisation" : "Member removed")
        if (isSelf) {
          navigateWithThreadPreservation(navigate, homePath, searchParams)
        }
      },
      onError: (err) => {
        const message = err instanceof Error ? err.message : "Failed to remove member"
        toast.error(message)
      },
    })
  }

  const handleRevokeInvitation = (invitationId: string, email: string) => {
    if (!window.confirm(`Revoke invitation for ${email}?`)) return

    revokeInvitation(invitationId, {
      onSuccess: () => toast.success("Invitation revoked"),
      onError: (err) => {
        const message = err instanceof Error ? err.message : "Failed to revoke invitation"
        toast.error(message)
      },
    })
  }

  if (!routeSlug) {
    return (
      <SettingsPageShell title={teamSection?.label ?? "Team"}>
        <div className="text-center text-sm text-muted-foreground">Missing organisation.</div>
      </SettingsPageShell>
    )
  }

  if (isLoading && !organisation) {
    return (
      <SettingsPageShell
        title={teamSection?.label ?? "Team"}
        description={teamSection?.description}
      >
        <EntityDetailSkeleton variant="settings" withPageContainer={false} ariaLabel="Loading team settings" />
      </SettingsPageShell>
    )
  }

  if (error || !organisation) {
    return (
      <SettingsPageShell title="Organisation not found">
        <div className="space-y-4 text-center">
          <p className="text-sm text-muted-foreground">
            This organisation does not exist or you do not have access to it.
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigateWithThreadPreservation(navigate, homePath, searchParams)}
          >
            Back to organisation
          </Button>
        </div>
      </SettingsPageShell>
    )
  }

  return (
    <SettingsPageShell
      title={teamSection?.label ?? "Team"}
      description={teamSection?.description}
    >
      <SettingsPanel>
          <InviteByEmailField
            value={inviteEmail}
            onChange={setInviteEmail}
            onSubmit={handleInvite}
            isSubmitting={isInviting}
            className="px-4 py-4"
          />

          {invitationsError instanceof Error ? (
            <div
              role="alert"
              className="border-b border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive"
            >
              Could not load pending invites. {invitationsError.message}
            </div>
          ) : null}

          {isLoadingTeam ? (
            <SettingsPanelEmpty>Loading team…</SettingsPanelEmpty>
          ) : teamList.length === 0 ? (
            <SettingsPanelEmpty>No team members yet.</SettingsPanelEmpty>
          ) : (
            <ul>
              {teamList.map((item) => {
                if (item.kind === "member") {
                  const { member } = item
                  const removeDisabled = isLastMember || isRemovingMember
                  return (
                    <li key={item.key}>
                      <div className="flex flex-col gap-2 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                        <PersonIdentityCell
                          name={member.full_name}
                          subline={member.email}
                          avatarUrl={member.avatar_url}
                          colorSeed={member.id}
                          nameAside={
                            member.is_self ? (
                              <Badge variant="secondary" className="text-xs font-normal">
                                You
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs font-normal">
                                Member
                              </Badge>
                            )
                          }
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={removeDisabled}
                          title={
                            isLastMember
                              ? "An organisation must keep at least one member"
                              : undefined
                          }
                          onClick={() => handleRemoveMember(member.id, member.email, member.is_self)}
                        >
                          <UserMinus className="mr-2 h-4 w-4" />
                          {member.is_self ? "Leave" : "Remove"}
                        </Button>
                      </div>
                    </li>
                  )
                }

                const { invitation } = item
                const isSendingInvite = Boolean(invitation.isOptimistic)
                return (
                  <li key={item.key}>
                    <div
                      className="flex flex-col gap-2 px-4 py-4 sm:flex-row sm:items-center sm:justify-between"
                      aria-busy={isSendingInvite || undefined}
                    >
                      <PersonIdentityCell
                        name={invitation.email}
                        subline={
                          isSendingInvite
                            ? "Sending invite…"
                            : `Expires ${new Date(invitation.expires_at).toLocaleString()}`
                        }
                        colorSeed={invitation.email}
                        avatarClassName="opacity-80"
                        nameAside={
                          <Badge variant="secondary" className="text-xs font-normal">
                            {isSendingInvite ? (
                              <span className="inline-flex items-center gap-1.5">
                                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                                Pending
                              </span>
                            ) : (
                              "Pending"
                            )}
                          </Badge>
                        }
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="iconSm"
                        aria-label="Revoke invitation"
                        title="Revoke invitation"
                        disabled={isRevokingInvitation || isSendingInvite}
                        onClick={() => handleRevokeInvitation(invitation.id, invitation.email)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
      </SettingsPanel>
    </SettingsPageShell>
  )
}
